// The machine's files (spec "Files"): the hub's /api/files/<op>, the agent's file API that Finder
// on the Mac uses too (internal/agent/files.go). Items are known by IDs that survive renames; the
// home's is "home". A file's bytes come as octet-stream and are never shown as a page of this site:
// previews read them into a Blob of a type this side picks (filePreview.ts).

import { api } from '../api';

export interface FileItem {
  id: string;
  parent: string;
  name: string;
  kind: 'file' | 'dir' | 'link';
  size: number;
  /** Nanoseconds since 1970. */
  mtime: number;
  mode: number;
  target?: string;
  /** Relative to the home (Recent only). */
  path?: string;
}

export const HOME = 'home';

const q = (params: Record<string, string>) => new URLSearchParams(params).toString();

async function call<T>(method: 'GET' | 'POST', op: string, params: Record<string, string>): Promise<T> {
  const r = await api<T>(method, `/api/files/${op}?${q(params)}`);
  if (!r.ok) throw new Error(r.error);
  return r.data;
}

/** A folder's items: folders first, then files, each by name as Finder sorts (numbers in order). */
export async function list(id: string): Promise<FileItem[]> {
  const r = await call<{ items: FileItem[] | null }>('GET', 'list', { id });
  return sortItems(r.items ?? []);
}

export const sortItems = (items: FileItem[]) =>
  [...items].sort((a, b) => Number(b.kind === 'dir') - Number(a.kind === 'dir') || a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

/** The home, each folder down to id, and id: its breadcrumbs. */
export async function path(id: string): Promise<FileItem[]> {
  return (await call<{ items: FileItem[] | null }>('GET', 'path', { id })).items ?? [];
}

/** The files changed lately anywhere in the home, newest first. */
export async function recent(limit = 12): Promise<FileItem[]> {
  return (await call<{ items: FileItem[] | null }>('GET', 'recent', { limit: String(limit) })).items ?? [];
}

export const mkdir = (parent: string, name: string) => call<FileItem>('POST', 'mkdir', { parent, name });
export const move = (id: string, parent: string, name: string) => call<FileItem>('POST', 'move', { id, parent, name });
export const trash = (id: string) => call<object>('POST', 'trash', { id });

/** Where the file downloads from: the agent names it (Content-Disposition), so the browser saves it. */
export const downloadURL = (id: string) => `/api/files/read?${q({ id, download: '1' })}`;

/** Starts a download of the file in this browser. */
export function download(item: FileItem) {
  const a = document.createElement('a');
  a.href = downloadURL(item.id);
  a.download = item.name;
  document.body.append(a);
  a.click();
  a.remove();
}

/** The file's bytes, at most `max` of them (a Range read), for a preview. */
export async function read(id: string, max: number): Promise<{ bytes: ArrayBuffer; cut: boolean }> {
  const r = await fetch(`/api/files/read?${q({ id })}`, { credentials: 'same-origin', cache: 'no-store', headers: { Range: `bytes=0-${max - 1}` } });
  if (!r.ok && r.status !== 206) throw new Error(`Couldn’t read it (HTTP ${r.status})`);
  const bytes = await r.arrayBuffer();
  const total = Number(r.headers.get('content-range')?.split('/')[1] ?? bytes.byteLength);
  return { bytes, cut: total > bytes.byteLength };
}

/** Uploads a file into the folder parent under its own name, reporting progress (0…1). A name
 *  that's taken gets " 2", " 3"… before its extension, as Finder does. */
export async function upload(parent: string, file: File, onProgress?: (p: number) => void): Promise<FileItem> {
  for (let n = 1; n < 100; n++) {
    const name = n === 1 ? file.name : numbered(file.name, n);
    const r = await put(`/api/files/write?${q({ parent, name })}`, file, onProgress);
    if (r.status === 409) continue;
    if (r.status >= 200 && r.status < 300) return JSON.parse(r.body) as FileItem;
    throw new Error(errorOf(r.body) ?? `Upload failed (HTTP ${r.status})`);
  }
  throw new Error('Too many files with that name');
}

export function numbered(name: string, n: number) {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? `${name.slice(0, dot)} ${n}${name.slice(dot)}` : `${name} ${n}`;
}

const errorOf = (body: string) => {
  try {
    const e = (JSON.parse(body) as { error?: string }).error;
    return e ? e[0].toUpperCase() + e.slice(1) : undefined;
  } catch {
    return undefined;
  }
};

/** A PUT with upload progress (fetch has none). */
function put(url: string, body: Blob, onProgress?: (p: number) => void): Promise<{ status: number; body: string }> {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('PUT', url);
    x.setRequestHeader('X-Cmx-Upload', '1'); // raw bytes, not JSON: the CSRF guard lets this through
    x.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    x.onload = () => resolve({ status: x.status, body: x.responseText });
    x.onerror = () => reject(new Error('Network error — check your connection and try again.'));
    x.send(body);
  });
}

/** What changed since the last call: items there now (their folders changed, and wherever they
 *  were), IDs gone, or expired (read everything again). */
export interface Changes {
  items?: FileItem[] | null;
  gone?: string[] | null;
  expired?: boolean;
}

/** Follows what changes in folders listed here (the agent watches them). Stops on abort. */
export async function follow(onChange: (c: Changes) => void, signal: AbortSignal) {
  let anchor = '';
  while (!signal.aborted) {
    try {
      const r = await fetch(`/api/files/changes?${q({ anchor, wait: anchor ? '25' : '0' })}`, { credentials: 'same-origin', cache: 'no-store', signal });
      if (!r.ok) throw new Error(String(r.status));
      const c = (await r.json()) as Changes & { anchor: string };
      if (anchor && (c.expired || c.items?.length || c.gone?.length)) onChange(c);
      anchor = c.anchor;
    } catch {
      if (signal.aborted) return;
      await new Promise((r) => setTimeout(r, 3000));
    }
  }
}

/** The path as people type it: ~/Downloads/report.csv. */
export const tildePath = (crumbs: { name: string }[], name?: string) => ['~', ...crumbs.slice(1).map((c) => c.name), ...(name ? [name] : [])].join('/');
